/**
 * Єдиний вхід.
 *
 * У застосунок заходять двоє різних людей: діловод — у кабінет, за логіном і
 * паролем; курсант — по свої рапорти, за одним кодом зі списку. Раніше
 * це були дві окремі сторінки, і на першу ж людину, яка вписала прізвище в
 * поле «Логін», це й розсипалося. Тепер сторінка одна, а перемикач нагорі
 * каже, хто саме заходить.
 */

import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { Segments } from '../components/Segments.js';
import { useNavigate } from 'react-router-dom';
import { api, errorText } from '../api/client.js';
import { useData } from '../store/data.js';
import { Logo } from '../components/ui.js';

interface AuthState {
  configured: boolean;
  orgs: Array<{ slug: string; shortName: string; name: string }>;
}

export type LoginMode = 'staff' | 'person';

export function LoginPage({ mode: initial = 'staff', onPersonLogin }: {
  mode?: LoginMode;
  onPersonLogin?: () => Promise<void>;
} = {}): ReactNode {
  const login = useData(s => s.login);
  const navigate = useNavigate();
  const [mode, setMode] = useState<LoginMode>(initial);
  const [state, setState] = useState<AuthState | null>(null);
  const [form, setForm] = useState({ login: '', password: '', org: '' });
  const [me, setMe] = useState({ code: '' });
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api
      .get<AuthState>('/api/auth/state')
      .then(s => {
        setState(s);
        if (s.orgs.length === 1) setForm(f => ({ ...f, org: s.orgs[0]!.slug }));
      })
      .catch(() => setState({ configured: true, orgs: [] }));
  }, []);

  const submit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setBusy(true);
    setError('');
    try {
      if (mode === 'staff') {
        await login(form.login.trim(), form.password, form.org || undefined);
        // Вхід діловода зі сторінки курсанта (/me): кабінет живе на корені,
        // а /me далі показував би той самий логін — сесія ж там інша.
        if (window.location.pathname.startsWith('/me')) navigate('/', { replace: true });
      } else {
        await api.post('/api/me/login', { code: me.code.trim(), org: form.org || undefined });
        // Сесія курсанта вже стоїть печивом — далі сторінка рапортів сама
        // спитає в сервера, хто зайшов.
        // На /me сторінка вже змонтована: перехід на ту саму адресу
        // не запускає її початкове завантаження сесії вдруге.
        if (onPersonLogin) await onPersonLogin();
        else navigate('/me', { replace: true });
      }
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  };

  const manyOrgs = (state?.orgs.length ?? 0) > 1;
  const staff = mode === 'staff';
  const ready = staff ? !!form.login && !!form.password : me.code.replace(/\D/g, '').length >= 8;

  const pick = (next: LoginMode): void => {
    setMode(next);
    setError('');
  };

  return (
    <div className="center-box login-box">
      <form className="login-card" onSubmit={e => void submit(e)}>
        <div className="login-head">
          <Logo />
        </div>

        {/* Дві ролі, дві половини — і видно, що вибрано, ще до полів. */}
        <Segments className="login-tabs" role="tablist" onClass="is-on">
          <button
            type="button"
            role="tab"
            aria-selected={staff}
            className={`login-tab${staff ? ' is-on' : ''}`}
            onClick={() => pick('staff')}
          >
            Діловод
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={!staff}
            className={`login-tab${staff ? '' : ' is-on'}`}
            onClick={() => pick('person')}
          >
            Мої рапорти
          </button>
        </Segments>

        {manyOrgs ? (
          <div className="field">
            <label>Організація</label>
            <select className="select" value={form.org} onChange={e => setForm({ ...form, org: e.target.value })}>
              {state!.orgs.map(o => (
                <option key={o.slug} value={o.slug}>
                  {o.shortName}
                </option>
              ))}
            </select>
          </div>
        ) : null}

        {/* Обидві вкладки живуть в одній коробці зі сталою висотою: у «Діловода»
            полів два, у «Моїх рапортах» одне, і без неї картка підстрибувала
            на кожному перемиканні. */}
        <div className="login-fields">
          {staff ? (
            <>
              <div className="field">
                <label>Логін</label>
                <input
                  className="input"
                  autoComplete="username"
                  autoFocus
                  value={form.login}
                  onChange={e => setForm({ ...form, login: e.target.value })}
                />
              </div>
              <div className="field">
                <label>Пароль</label>
                <input
                  className="input"
                  type="password"
                  autoComplete="current-password"
                  value={form.password}
                  onChange={e => setForm({ ...form, password: e.target.value })}
                />
              </div>
            </>
          ) : (
            <div className="field">
              <label>Ідентифікаційний код</label>
              {/* Закрито крапками, як пароль: код — єдиний ключ до персональних
                  даних людини, і підглянути його через плече не має бути легше,
                  ніж пароль діловода. Клавіатура лишається цифровою. */}
              <input
                className="input"
                type="password"
                inputMode="numeric"
                autoComplete="off"
                autoFocus
                value={me.code}
                onChange={e => setMe({ code: e.target.value })}
              />
            </div>
          )}
        </div>

        {error ? <p className="error-text">{error}</p> : null}

        <button className="btn primary login-go" disabled={busy || !ready}>
          {busy ? 'Заходжу…' : 'Увійти'}
        </button>
      </form>
    </div>
  );
}
